// utils/caseHelpers.js
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";

export async function deleteCaseIfNoKits(caseId, { dryRun = false } = {}) {
	let issues = [];

	// Check for associated kits
	const { data: kitLinks, error: linkError } = await supabase
		.from("kit_cases")
		.select("id")
		.eq("case_id", caseId);

	if (linkError) {
		console.error("Failed to check kit associations:", linkError);
		return {
			success: false,
			message: "Failed to check for associated kits.",
		};
	}

	if (kitLinks && kitLinks.length > 0) {
		issues.push(
			"This case is still part of a kit. Remove it from all kits first."
		);
	}

	// Check for associated gear
	const { data: gearItems, error: gearError } = await supabase
		.from("gear")
		.select("id")
		.eq("case_id", caseId);

	if (gearError) {
		console.error("Failed to check gear associations:", gearError);
		return {
			success: false,
			message: "Failed to check for associated gear.",
		};
	}

	if (gearItems && gearItems.length > 0) {
		issues.push(
			"This case still has gear in it. Move or delete all gear before proceeding."
		);
	}

	if (issues.length > 0) {
		return {
			success: false,
			message: issues.join(" "),
		};
	}

	if (dryRun) {
		// Passed all checks but not actually deleting
		return { success: true };
	}

	// Perform deletion
	const { error: deleteError } = await supabase
		.from("cases")
		.delete()
		.eq("id", caseId);

	if (deleteError) {
		console.error("Case deletion failed:", deleteError);
		return { success: false, message: "Failed to delete case." };
	}

	return { success: true };
}

/**
 * Duplicate a case and its gear items.
 * @param {string} caseId - ID of the case to duplicate
 * @param {function} navigate - (optional) React router navigate function
 * @returns {Promise<{ success: boolean, newCaseId?: string, error?: string }>}
 */
export async function duplicateCase(caseId, navigate) {
	const toastId = toast.loading("Duplicating case...");

	try {
		// Get original case
		const { data: originalCase, error: caseError } = await supabase
			.from("cases")
			.select("*")
			.eq("id", caseId)
			.single();

		if (caseError) throw caseError;

		// Create new case
		const { data: newCase, error: newCaseError } = await supabase
			.from("cases")
			.insert([
				{
					name: `Copy of ${originalCase.name}`,
					weight: originalCase.weight,
					notes: originalCase.notes,
					image_url: originalCase.image_url,
				},
			])
			.select()
			.single();

		if (newCaseError) throw newCaseError;

		// Copy gear items
		const { data: gearItems, error: gearError } = await supabase
			.from("gear")
			.select("*")
			.eq("case_id", caseId);

		if (gearError) throw gearError;

		if (gearItems.length > 0) {
			const duplicatedGear = gearItems.map(
				({ id, created_at, ...rest }) => ({
					...rest,
					case_id: newCase.id,
				})
			);

			const { error: insertError } = await supabase
				.from("gear")
				.insert(duplicatedGear);

			if (insertError) throw insertError;
		}

		toast.success("Case duplicated!", { id: toastId });

		if (navigate) {
			navigate(`/cases/${newCase.id}`);
		}

		return { success: true, newCaseId: newCase.id };
	} catch (err) {
		console.error("Duplicate error:", err);
		toast.error("Failed to duplicate case: " + err.message, {
			id: toastId,
		});
		return { success: false, error: err.message };
	}
}
